import React, { useState, useMemo, useCallback } from 'react';
import ShowcasePageWrapper from '../shared/ShowcasePageWrapper';
import { generateTimelineData, SLDS_COLORS } from '../shared/SampleData';
import MetricCard from '../shared/MetricCard';
import { SparkLine, MiniDonut } from '../shared/ChartComponents';
import Button from '@salesforce/design-system-react/components/button';
import ButtonGroup from '@salesforce/design-system-react/components/button-group';
import Icon from '@salesforce/design-system-react/components/icon';
import Input from '@salesforce/design-system-react/components/input';
import InputIcon from '@salesforce/design-system-react/components/icon/input-icon';
import Combobox from '@salesforce/design-system-react/components/combobox';
import Modal from '@salesforce/design-system-react/components/modal';
import Pill from '@salesforce/design-system-react/components/pill';
import Checkbox from '@salesforce/design-system-react/components/checkbox';
import DataTable from '@salesforce/design-system-react/components/data-table';
import DataTableColumn from '@salesforce/design-system-react/components/data-table/column';
import DataTableCell from '@salesforce/design-system-react/components/data-table/cell';
import Tooltip from '@salesforce/design-system-react/components/tooltip';
import Toast from '@salesforce/design-system-react/components/toast';
import ToastContainer from '@salesforce/design-system-react/components/toast/container';

// ========== 1. Constants & Configuration ==========

const TYPE_CONFIG = {
  call:                { label: 'Call',                icon: 'call',               iconCategory: 'standard', color: '#0176d3', bgColor: '#0176d314' },
  email:               { label: 'Email',               icon: 'email',              iconCategory: 'standard', color: '#9050e9', bgColor: '#9050e914' },
  meeting:             { label: 'Meeting',             icon: 'event',              iconCategory: 'standard', color: '#2e844a', bgColor: '#2e844a14' },
  task:                { label: 'Task',                icon: 'task',               iconCategory: 'standard', color: '#fe9339', bgColor: '#fe933914' },
  stage_change:        { label: 'Stage Change',        icon: 'opportunity',        iconCategory: 'standard', color: '#c23934', bgColor: '#c2393414' },
  case:                { label: 'Case',                icon: 'case_log_a_call',    iconCategory: 'standard', color: '#e8006f', bgColor: '#e8006f14' },
  order:               { label: 'Order',               icon: 'orders',             iconCategory: 'standard', color: '#0d9dda', bgColor: '#0d9dda14' },
  note:                { label: 'Note',                icon: 'note',               iconCategory: 'standard', color: '#747474', bgColor: '#74747414' },
  attachment:          { label: 'Attachment',           icon: 'attach',             iconCategory: 'utility',  color: '#baac93', bgColor: '#baac9314' },
  site_visit:          { label: 'Site Visit',          icon: 'location',           iconCategory: 'standard', color: '#06a59a', bgColor: '#06a59a14' },
  escalation:          { label: 'Escalation',          icon: 'warning',            iconCategory: 'utility',  color: '#c23934', bgColor: '#c2393414' },
  subscription_change: { label: 'Subscription Change', icon: 'change_record_type', iconCategory: 'utility',  color: '#9050e9', bgColor: '#9050e914' },
  approval:            { label: 'Approval',            icon: 'approval',           iconCategory: 'standard', color: '#0176d3', bgColor: '#0176d314' },
};

const PRIORITY_COLORS = { Critical: '#c23934', High: '#fe9339', Medium: '#0176d3', Low: '#706e6b' };
const OUTCOME_COLORS = { Connected: '#2e844a', Voicemail: '#fe9339', 'No Answer': '#c23934', 'Left Message': '#0176d3', Busy: '#706e6b' };
const STATUS_COLORS = { New: '#0176d3', Working: '#fe9339', Escalated: '#c23934', 'Waiting on Customer': '#706e6b', Closed: '#2e844a', Pending: '#fe9339', Approved: '#2e844a', Rejected: '#c23934' };
const SEVERITY_COLORS = { P1: '#c23934', P2: '#fe9339', P3: '#0176d3' };
const CHANGE_TYPE_COLORS = { Upgrade: '#2e844a', Downgrade: '#fe9339', Cancel: '#c23934', Renewal: '#0176d3' };

const SORT_OPTIONS = [
  { id: 'date_desc', label: 'Date (Newest)' },
  { id: 'date_asc', label: 'Date (Oldest)' },
  { id: 'priority', label: 'Priority' },
  { id: 'type', label: 'Record Type' },
];

const GROUP_OPTIONS = [
  { id: 'none', label: 'No Grouping' },
  { id: 'date', label: 'Date' },
  { id: 'type', label: 'Record Type' },
  { id: 'user', label: 'User' },
];

const cardStyle = { backgroundColor: 'white', border: '1px solid #dddbda', borderRadius: '4px', padding: '20px' };
const toolbarStyle = { ...cardStyle, marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap', padding: '12px 20px' };

// ========== 2. Utility Functions ==========

const REF_NOW = new Date(2025, 6, 28, 17, 0);

const formatRelativeTime = (isoString) => {
  const then = new Date(isoString);
  const diffMs = REF_NOW - then;
  const diffMin = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  if (diffMin < 1) return 'Just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays}d ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`;
  return then.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const formatAbsoluteTime = (isoString) => {
  const d = new Date(isoString);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) + ' at ' + d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
};

const formatCurrency = (amount) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(amount);

const formatDate = (dateString) => new Date(dateString).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

const getDateGroup = (isoString) => {
  const then = new Date(isoString);
  const diffDays = Math.floor((REF_NOW - then) / 86400000);
  if (diffDays < 1) return 'Today';
  if (diffDays < 2) return 'Yesterday';
  if (diffDays < 7) return 'This Week';
  if (diffDays < 30) return 'This Month';
  return 'Older';
};

const groupActivities = (activities, groupBy) => {
  if (groupBy === 'none') return [{ label: null, items: activities }];
  const map = {};
  const order = [];
  activities.forEach((a) => {
    let key;
    if (groupBy === 'date') key = getDateGroup(a.timestamp);
    else if (groupBy === 'type') key = TYPE_CONFIG[a.type]?.label || a.type;
    else if (groupBy === 'user') key = a.user;
    else key = 'Other';
    if (!map[key]) { map[key] = []; order.push(key); }
    map[key].push(a);
  });
  return order.map((label) => ({ label, items: map[label], count: map[label].length }));
};

const priorityRank = { Critical: 0, High: 1, Medium: 2, Low: 3 };

// ========== 3. Analytics Summary Panel ==========

const AnalyticsSummaryPanel = ({ activities, visible, onToggle }) => {
  const stats = useMemo(() => {
    const emailsSent = activities.filter((a) => a.type === 'email' && a.direction === 'sent').length;
    const callsMade = activities.filter((a) => a.type === 'call').length;
    const openTasks = activities.filter((a) => a.type === 'task' && !a.completed).length;
    const total = activities.length;

    // Avg response time (mock)
    const avgResponseHrs = Math.round((24 + Math.random() * 48) * 10) / 10;

    // Sparkline: 30-day trend
    const trend = Array.from({ length: 30 }, (_, i) => {
      const dayStart = new Date(REF_NOW.getTime() - (29 - i) * 86400000);
      const dayEnd = new Date(dayStart.getTime() + 86400000);
      return activities.filter((a) => { const t = new Date(a.timestamp); return t >= dayStart && t < dayEnd; }).length;
    });

    // Type breakdown for donut
    const typeCounts = {};
    activities.forEach((a) => { typeCounts[a.type] = (typeCounts[a.type] || 0) + 1; });

    // Last engagement
    const lastEngagement = activities.length > 0 ? activities[0].timestamp : null;

    return { total, emailsSent, callsMade, openTasks, avgResponseHrs, trend, typeCounts, lastEngagement };
  }, [activities]);

  if (!visible) {
    return (
      <div style={{ ...cardStyle, marginBottom: '16px', padding: '12px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontSize: '13px', fontWeight: '600', color: '#3e3e3c' }}>Analytics Summary</span>
        <Button label="Show" variant="base" onClick={onToggle} iconCategory="utility" iconName="chevrondown" iconPosition="right" iconSize="x-small" />
      </div>
    );
  }

  const topTypes = Object.entries(stats.typeCounts).sort((a, b) => b[1] - a[1]).slice(0, 5);

  return (
    <div style={{ ...cardStyle, marginBottom: '16px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <h3 style={{ fontSize: '14px', fontWeight: '700', color: '#080707', margin: 0 }}>Analytics Summary</h3>
        <Button label="Hide" variant="base" onClick={onToggle} iconCategory="utility" iconName="chevronup" iconPosition="right" iconSize="x-small" />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: '12px', marginBottom: '20px' }}>
        <MetricCard title="Total Activities" value={stats.total} format="number" icon="activity" />
        <MetricCard title="Emails Sent" value={stats.emailsSent} format="number" icon="email" iconCategory="standard" />
        <MetricCard title="Calls Made" value={stats.callsMade} format="number" icon="call" iconCategory="standard" />
        <MetricCard title="Open Tasks" value={stats.openTasks} format="number" icon="task" iconCategory="standard" />
        <MetricCard title="Avg Response (hrs)" value={stats.avgResponseHrs} format="number" icon="clock" />
      </div>

      <div style={{ display: 'flex', gap: '24px', alignItems: 'center', flexWrap: 'wrap' }}>
        {/* 30-day engagement trend */}
        <div style={{ flex: '1 1 200px' }}>
          <div style={{ fontSize: '12px', color: '#706e6b', marginBottom: '4px', fontWeight: '600' }}>30-Day Engagement Trend</div>
          <SparkLine data={stats.trend} color="#0176d3" width={200} height={40} />
        </div>

        {/* Type breakdown */}
        <div style={{ flex: '0 0 auto', display: 'flex', alignItems: 'center', gap: '16px' }}>
          <MiniDonut value={stats.emailsSent + stats.callsMade} total={stats.total} color="#0176d3" size={56} />
          <div>
            <div style={{ fontSize: '12px', color: '#706e6b', fontWeight: '600', marginBottom: '4px' }}>Top Types</div>
            {topTypes.map(([type, count]) => (
              <div key={type} style={{ fontSize: '11px', color: '#3e3e3c', display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '2px' }}>
                <span style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: TYPE_CONFIG[type]?.color || '#706e6b', display: 'inline-block' }} />
                {TYPE_CONFIG[type]?.label || type}: {count}
              </div>
            ))}
          </div>
        </div>

        {/* Last engagement */}
        {stats.lastEngagement && (
          <div style={{ flex: '0 0 auto', padding: '8px 16px', borderRadius: '20px', backgroundColor: '#eef4ff', border: '1px solid #b9d6f7' }}>
            <div style={{ fontSize: '11px', color: '#706e6b', fontWeight: '600' }}>Last Engagement</div>
            <div style={{ fontSize: '13px', color: '#0176d3', fontWeight: '600' }}>{formatRelativeTime(stats.lastEngagement)}</div>
          </div>
        )}
      </div>
    </div>
  );
};

// ========== 4. Filter Bar ==========

const FilterBar = ({ activeFilters, onRemoveFilter, onClearAll, onAddFilter, onSmartFilter, searchTerm, onSearchChange }) => {
  const [showAddFilter, setShowAddFilter] = useState(false);
  const [filterCategory, setFilterCategory] = useState(null);
  const [typeSelections, setTypeSelections] = useState({});

  const handleTypeToggle = (type) => {
    setTypeSelections((prev) => ({ ...prev, [type]: !prev[type] }));
  };

  const applyTypeFilter = () => {
    const selected = Object.entries(typeSelections).filter(([, v]) => v).map(([k]) => k);
    if (selected.length > 0) onAddFilter({ category: 'type', values: selected });
    setShowAddFilter(false);
    setFilterCategory(null);
    setTypeSelections({});
  };

  return (
    <div style={{ ...cardStyle, marginBottom: '16px', padding: '12px 20px' }}>
      {/* Active filter pills */}
      {activeFilters.length > 0 && (
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center', marginBottom: '12px' }}>
          <span style={{ fontSize: '12px', color: '#706e6b', fontWeight: '600' }}>Active Filters:</span>
          {activeFilters.map((filter, idx) => (
            <Pill
              key={`${filter.category}-${filter.label}-${idx}`}
              labels={{ label: filter.label }}
              onClick={() => onRemoveFilter(filter)}
              onRemove={() => onRemoveFilter(filter)}
            />
          ))}
          <Button label="Clear All" variant="base" onClick={onClearAll} />
        </div>
      )}

      <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
        {/* Add Filter button */}
        <div style={{ position: 'relative' }}>
          <Button label="+ Add Filter" variant="neutral" onClick={() => setShowAddFilter(!showAddFilter)} />
          {showAddFilter && (
            <div style={{ position: 'absolute', top: '100%', left: 0, zIndex: 100, marginTop: '4px', backgroundColor: 'white', border: '1px solid #dddbda', borderRadius: '4px', boxShadow: '0 2px 12px rgba(0,0,0,0.15)', minWidth: '280px', padding: '12px' }}>
              {!filterCategory ? (
                <div>
                  <div style={{ fontSize: '12px', fontWeight: '600', color: '#706e6b', marginBottom: '8px', textTransform: 'uppercase' }}>Filter By</div>
                  {['Record Type', 'Priority', 'Status'].map((cat) => (
                    <div
                      key={cat}
                      onClick={() => setFilterCategory(cat)}
                      style={{ padding: '8px 12px', cursor: 'pointer', borderRadius: '4px', fontSize: '13px', color: '#3e3e3c' }}
                      onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f3f3f3'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                    >
                      {cat}
                    </div>
                  ))}
                </div>
              ) : filterCategory === 'Record Type' ? (
                <div>
                  <div style={{ fontSize: '12px', fontWeight: '600', color: '#706e6b', marginBottom: '8px' }}>
                    <span style={{ cursor: 'pointer', color: '#0176d3' }} onClick={() => setFilterCategory(null)}>&larr; Back</span> &middot; Record Type
                  </div>
                  <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                    {Object.entries(TYPE_CONFIG).map(([key, cfg]) => (
                      <div key={key} style={{ padding: '4px 0' }}>
                        <Checkbox
                          labels={{ label: cfg.label }}
                          checked={!!typeSelections[key]}
                          onChange={() => handleTypeToggle(key)}
                        />
                      </div>
                    ))}
                  </div>
                  <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid #f3f3f3' }}>
                    <Button label="Apply" variant="brand" onClick={applyTypeFilter} />
                  </div>
                </div>
              ) : (
                <div>
                  <div style={{ fontSize: '12px', fontWeight: '600', color: '#706e6b', marginBottom: '8px' }}>
                    <span style={{ cursor: 'pointer', color: '#0176d3' }} onClick={() => setFilterCategory(null)}>&larr; Back</span> &middot; {filterCategory}
                  </div>
                  {(filterCategory === 'Priority' ? ['Critical', 'High', 'Medium', 'Low'] : ['New', 'Working', 'Escalated', 'Closed', 'Pending', 'Approved', 'Rejected']).map((val) => (
                    <div
                      key={val}
                      onClick={() => { onAddFilter({ category: filterCategory.toLowerCase(), values: [val] }); setShowAddFilter(false); setFilterCategory(null); }}
                      style={{ padding: '8px 12px', cursor: 'pointer', borderRadius: '4px', fontSize: '13px', color: '#3e3e3c' }}
                      onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f3f3f3'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                    >
                      {val}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Smart filter buttons */}
        <Button label="Needs Attention" variant="neutral" onClick={() => onSmartFilter('needs_attention')} iconCategory="utility" iconName="warning" iconPosition="left" iconSize="x-small" />
        <Button label="This Week" variant="neutral" onClick={() => onSmartFilter('this_week')} iconCategory="utility" iconName="date_input" iconPosition="left" iconSize="x-small" />
        <Button label="My Activities" variant="neutral" onClick={() => onSmartFilter('my_activities')} iconCategory="utility" iconName="user" iconPosition="left" iconSize="x-small" />

        {/* Search */}
        <div style={{ flex: '1 1 200px', minWidth: '180px' }}>
          <Input
            assistiveText={{ label: 'Search Activities' }}
            placeholder="Search activities..."
            value={searchTerm}
            onChange={(e, { value }) => onSearchChange(value)}
            iconLeft={<InputIcon name="search" category="utility" />}
            iconRight={searchTerm ? <InputIcon name="clear" category="utility" onClick={() => onSearchChange('')} /> : undefined}
          />
        </div>
      </div>
    </div>
  );
};

// ========== 5. Sort & Group Bar ==========

const SortGroupBar = ({ sortBy, onSortChange, groupBy, onGroupChange, viewMode, onViewModeChange, includeRelated, onToggleRelated, totalCount, expandAll, onToggleExpandAll }) => (
  <div style={toolbarStyle}>
    <div style={{ flex: '0 0 170px' }}>
      <Combobox
        labels={{ label: 'Sort by', placeholder: 'Sort...' }}
        options={SORT_OPTIONS.map((o) => ({ ...o, value: o.id }))}
        selection={SORT_OPTIONS.filter((o) => o.id === sortBy)}
        variant="readonly"
        events={{ onSelect: (e, data) => { if (data.selection.length > 0) onSortChange(data.selection[0].id); } }}
      />
    </div>
    <div style={{ flex: '0 0 170px' }}>
      <Combobox
        labels={{ label: 'Group by', placeholder: 'Group...' }}
        options={GROUP_OPTIONS.map((o) => ({ ...o, value: o.id }))}
        selection={GROUP_OPTIONS.filter((o) => o.id === groupBy)}
        variant="readonly"
        events={{ onSelect: (e, data) => { if (data.selection.length > 0) onGroupChange(data.selection[0].id); } }}
      />
    </div>

    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      <ButtonGroup>
        {[['Feed', 'feed'], ['Compact', 'compact'], ['Visual', 'visual']].map(([label, mode]) => (
          <Button key={mode} label={label} variant={viewMode === mode ? 'brand' : 'neutral'} onClick={() => onViewModeChange(mode)} />
        ))}
      </ButtonGroup>
    </div>

    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
      <Checkbox labels={{ label: 'Include Related' }} checked={includeRelated} onChange={() => onToggleRelated()} />
    </div>

    <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '12px' }}>
      <span style={{ fontSize: '13px', color: '#706e6b' }}>{totalCount} results</span>
      <Button label={expandAll ? 'Collapse All' : 'Expand All'} variant="base" onClick={onToggleExpandAll} iconCategory="utility" iconName={expandAll ? 'chevronup' : 'chevrondown'} iconPosition="right" iconSize="x-small" />
    </div>
  </div>
);

// ========== 6. Quick Add Bar ==========

const QuickAddBar = ({ onQuickAdd }) => (
  <div style={{ ...cardStyle, marginBottom: '16px', padding: '10px 20px', display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
    <span style={{ fontSize: '12px', fontWeight: '600', color: '#706e6b', marginRight: '4px' }}>Quick Add:</span>
    {[
      { type: 'call', label: 'Log Call', icon: 'call' },
      { type: 'email', label: 'Email', icon: 'email' },
      { type: 'task', label: 'Task', icon: 'task' },
      { type: 'note', label: 'Note', icon: 'note' },
      { type: 'meeting', label: 'Meeting', icon: 'event' },
      { type: 'case', label: 'Log Case', icon: 'case_log_a_call' },
    ].map((item) => (
      <Button key={item.type} label={`+ ${item.label}`} variant="neutral" onClick={() => onQuickAdd()} />
    ))}
  </div>
);

// ========== 7. Log Activity Modal (2-step) ==========

const LOG_ACTIVITY_TYPES = [
  { type: 'call',    label: 'Log a Call',  icon: 'call',            iconCategory: 'standard', color: '#0176d3', desc: 'Record a phone call' },
  { type: 'email',   label: 'Email',       icon: 'email',           iconCategory: 'standard', color: '#9050e9', desc: 'Log an email' },
  { type: 'meeting', label: 'Meeting',     icon: 'event',           iconCategory: 'standard', color: '#2e844a', desc: 'Log a meeting' },
  { type: 'task',    label: 'Task',        icon: 'task',            iconCategory: 'standard', color: '#fe9339', desc: 'Create a task' },
  { type: 'note',    label: 'Note',        icon: 'note',            iconCategory: 'standard', color: '#747474', desc: 'Add a note' },
  { type: 'case',    label: 'Log a Case',  icon: 'case_log_a_call', iconCategory: 'standard', color: '#e8006f', desc: 'Log a support case' },
];

const FORM_FIELDS = {
  call:    [{ key: 'subject',  label: 'Subject',            placeholder: 'Call with...' },
            { key: 'contact',  label: 'Contact',            placeholder: 'Who did you call?' },
            { key: 'duration', label: 'Duration (minutes)', placeholder: '15' },
            { key: 'outcome',  label: 'Outcome',            placeholder: 'Connected / Voicemail / No Answer', type: 'select', options: ['Connected', 'Voicemail', 'No Answer', 'Left Message'] },
            { key: 'notes',    label: 'Notes',              placeholder: 'Key takeaways...', type: 'textarea' }],
  email:   [{ key: 'subject',  label: 'Subject',            placeholder: 'Re: ...' },
            { key: 'to',       label: 'To',                 placeholder: 'Recipient name or email' },
            { key: 'body',     label: 'Body',               placeholder: 'Email content...', type: 'textarea' }],
  meeting: [{ key: 'title',    label: 'Meeting Title',      placeholder: 'Quarterly Review' },
            { key: 'location', label: 'Location',           placeholder: 'Zoom / Conference Room' },
            { key: 'duration', label: 'Duration (minutes)', placeholder: '60' },
            { key: 'attendees',label: 'Attendees',          placeholder: 'Names, comma-separated' },
            { key: 'notes',    label: 'Action Items',       placeholder: 'Follow-up actions...', type: 'textarea' }],
  task:    [{ key: 'title',    label: 'Task Title',         placeholder: 'Send proposal...' },
            { key: 'dueDate',  label: 'Due Date',           placeholder: '2025-08-01' },
            { key: 'priority', label: 'Priority',           placeholder: 'High', type: 'select', options: ['Critical', 'High', 'Medium', 'Low'] },
            { key: 'description', label: 'Description',     placeholder: 'Task details...', type: 'textarea' }],
  note:    [{ key: 'title',    label: 'Title',              placeholder: 'Meeting notes...' },
            { key: 'body',     label: 'Note',               placeholder: 'Write your note...', type: 'textarea' }],
  case:    [{ key: 'subject',  label: 'Subject',            placeholder: 'Issue description' },
            { key: 'priority', label: 'Priority',           placeholder: 'High', type: 'select', options: ['Critical', 'High', 'Medium', 'Low'] },
            { key: 'origin',   label: 'Origin',             placeholder: 'Phone', type: 'select', options: ['Web', 'Phone', 'Email', 'Chat'] },
            { key: 'description', label: 'Description',     placeholder: 'Describe the issue...', type: 'textarea' }],
};

const LogActivityModal = ({ isOpen, onClose, onSave }) => {
  const [step, setStep] = useState(1);
  const [selectedType, setSelectedType] = useState(null);
  const [formData, setFormData] = useState({});
  const [hovered, setHovered] = useState(null);

  const handleSelectType = (type) => {
    setSelectedType(type);
    setFormData({});
    setStep(2);
  };

  const handleBack = () => {
    setStep(1);
    setSelectedType(null);
    setFormData({});
  };

  const handleClose = () => {
    setStep(1);
    setSelectedType(null);
    setFormData({});
    onClose();
  };

  const handleSave = () => {
    onSave(selectedType, formData);
    handleClose();
  };

  if (!isOpen) return null;

  const typeConfig = selectedType ? LOG_ACTIVITY_TYPES.find((t) => t.type === selectedType) : null;
  const fields = selectedType ? (FORM_FIELDS[selectedType] || []) : [];

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={handleClose}
      heading={step === 1 ? 'Log Activity' : typeConfig?.label || 'New Activity'}
      footer={
        step === 1
          ? [<Button key="cancel" label="Cancel" onClick={handleClose} />]
          : [
              <Button key="back" label="Back" onClick={handleBack} iconCategory="utility" iconName="back" iconPosition="left" />,
              <Button key="save" label="Save" variant="brand" onClick={handleSave} />,
            ]
      }
      size="medium"
    >
      {step === 1 ? (
        /* Step 1: Type picker — big clean icon buttons */
        <div style={{ padding: '24px 32px 32px' }}>
          <p style={{ fontSize: '14px', color: '#706e6b', margin: '0 0 20px 0', textAlign: 'center' }}>What would you like to log?</p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
            {LOG_ACTIVITY_TYPES.map((item) => {
              const isHov = hovered === item.type;
              return (
                <div
                  key={item.type}
                  onClick={() => handleSelectType(item.type)}
                  onMouseEnter={() => setHovered(item.type)}
                  onMouseLeave={() => setHovered(null)}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '10px',
                    padding: '24px 12px',
                    borderRadius: '8px',
                    border: `2px solid ${isHov ? item.color : '#dddbda'}`,
                    backgroundColor: isHov ? `${item.color}0a` : 'white',
                    cursor: 'pointer',
                    transition: 'all 0.15s ease',
                    boxShadow: isHov ? `0 2px 8px ${item.color}22` : 'none',
                  }}
                >
                  <div style={{
                    width: '52px', height: '52px', borderRadius: '50%',
                    backgroundColor: isHov ? item.color : `${item.color}14`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    transition: 'background-color 0.15s ease',
                  }}>
                    <Icon category={item.iconCategory} name={item.icon} size="small" style={{ fill: isHov ? 'white' : item.color }} />
                  </div>
                  <span style={{ fontSize: '14px', fontWeight: '600', color: '#080707' }}>{item.label}</span>
                  <span style={{ fontSize: '12px', color: '#706e6b', textAlign: 'center' }}>{item.desc}</span>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        /* Step 2: Concise form for the selected type */
        <div style={{ padding: '20px 32px 28px' }}>
          {/* Type indicator */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px', paddingBottom: '16px', borderBottom: '1px solid #f3f3f3' }}>
            <div style={{ width: '36px', height: '36px', borderRadius: '50%', backgroundColor: typeConfig.color, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Icon category={typeConfig.iconCategory} name={typeConfig.icon} size="x-small" style={{ fill: 'white' }} />
            </div>
            <div>
              <div style={{ fontSize: '15px', fontWeight: '600', color: '#080707' }}>{typeConfig.label}</div>
              <div style={{ fontSize: '12px', color: '#706e6b' }}>{typeConfig.desc}</div>
            </div>
          </div>

          {/* Account field — always shown */}
          <div style={{ marginBottom: '14px' }}>
            <Input
              label="Account"
              placeholder="Account name..."
              value={formData.account || ''}
              onChange={(e, { value }) => setFormData((p) => ({ ...p, account: value }))}
            />
          </div>

          {/* Type-specific fields */}
          {fields.map((field) => (
            <div key={field.key} style={{ marginBottom: '14px' }}>
              {field.type === 'textarea' ? (
                <div>
                  <label style={{ display: 'block', fontSize: '12px', color: '#3e3e3c', fontWeight: '600', marginBottom: '4px' }}>{field.label}</label>
                  <textarea
                    value={formData[field.key] || ''}
                    onChange={(e) => setFormData((p) => ({ ...p, [field.key]: e.target.value }))}
                    placeholder={field.placeholder}
                    style={{ width: '100%', minHeight: '72px', padding: '10px', border: '1px solid #dddbda', borderRadius: '4px', fontSize: '13px', fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box' }}
                  />
                </div>
              ) : field.type === 'select' ? (
                <Combobox
                  labels={{ label: field.label, placeholder: field.placeholder }}
                  options={field.options.map((o) => ({ id: o, label: o, value: o }))}
                  selection={formData[field.key] ? [{ id: formData[field.key], label: formData[field.key] }] : []}
                  variant="readonly"
                  events={{ onSelect: (e, data) => { if (data.selection.length > 0) setFormData((p) => ({ ...p, [field.key]: data.selection[0].id })); } }}
                />
              ) : (
                <Input
                  label={field.label}
                  placeholder={field.placeholder}
                  value={formData[field.key] || ''}
                  onChange={(e, { value }) => setFormData((p) => ({ ...p, [field.key]: value }))}
                />
              )}
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
};

// ========== 8. Card Components — 13 Type-Specific Renderers ==========

const Badge = ({ label, color, bg }) => (
  <span style={{ display: 'inline-block', padding: '2px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '600', color: 'white', backgroundColor: color || '#706e6b', whiteSpace: 'nowrap' }}>
    {label}
  </span>
);

const FieldRow = ({ label, value }) => (
  <p style={{ margin: '0 0 6px 0', fontSize: '13px', color: '#3e3e3c' }}>
    <strong>{label}:</strong> {value}
  </p>
);

const ExpandedPanel = ({ children }) => (
  <div style={{ marginTop: '12px', padding: '12px', backgroundColor: '#f3f3f3', borderRadius: '4px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 16px' }}>
    {children}
  </div>
);

const CallCard = ({ activity, expanded }) => (
  <div>
    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
      <span style={{ fontSize: '13px', color: '#706e6b' }}>Duration: <strong style={{ color: '#080707' }}>{activity.duration} min</strong></span>
      <Badge label={activity.outcome} color={OUTCOME_COLORS[activity.outcome]} />
      {activity.sentiment && <span style={{ fontSize: '12px', color: activity.sentiment === 'Positive' ? '#2e844a' : activity.sentiment === 'Negative' ? '#c23934' : '#706e6b' }}>{activity.sentiment === 'Positive' ? '+' : activity.sentiment === 'Negative' ? '−' : '○'} {activity.sentiment}</span>}
    </div>
    {expanded && (
      <ExpandedPanel>
        <FieldRow label="Notes" value={activity.notes} />
        <FieldRow label="Contact" value={activity.contact || 'N/A'} />
        <FieldRow label="Account" value={activity.account} />
        <FieldRow label="Logged" value={formatAbsoluteTime(activity.timestamp)} />
      </ExpandedPanel>
    )}
  </div>
);

const EmailCard = ({ activity, expanded }) => (
  <div>
    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
      <span style={{ fontSize: '14px', fontWeight: '600', color: '#080707' }}>{activity.subject}</span>
      <span style={{ fontSize: '12px', color: '#706e6b', display: 'flex', alignItems: 'center', gap: '4px' }}>
        <Icon category="utility" name={activity.direction === 'sent' ? 'forward' : 'reply'} size="xx-small" style={{ fill: '#706e6b' }} />
        {activity.direction === 'sent' ? 'Sent' : 'Received'}
      </span>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '12px', color: activity.opened ? '#2e844a' : '#706e6b' }}>
        <span style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: activity.opened ? '#2e844a' : '#c23934', display: 'inline-block' }} />
        {activity.opened ? 'Opened' : 'Not opened'}
      </span>
      {activity.attachmentCount > 0 && (
        <span style={{ fontSize: '12px', color: '#706e6b', display: 'flex', alignItems: 'center', gap: '3px' }}>
          <Icon category="utility" name="attach" size="xx-small" style={{ fill: '#706e6b' }} /> {activity.attachmentCount}
        </span>
      )}
    </div>
    {expanded && (
      <ExpandedPanel>
        <FieldRow label="Subject" value={activity.subject} />
        <FieldRow label="Direction" value={activity.direction === 'sent' ? 'Outbound' : 'Inbound'} />
        <div style={{ gridColumn: '1 / -1' }}>
          <FieldRow label="Preview" value={activity.bodyPreview} />
        </div>
        <FieldRow label="Attachments" value={activity.attachmentCount || 'None'} />
        <FieldRow label="Status" value={activity.opened ? 'Opened by recipient' : 'Not yet opened'} />
      </ExpandedPanel>
    )}
  </div>
);

const MeetingCard = ({ activity, expanded }) => {
  const maxVisible = 3;
  const visibleAttendees = activity.attendees.slice(0, maxVisible);
  const remaining = activity.attendees.length - maxVisible;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
        <span style={{ fontSize: '14px', fontWeight: '600', color: '#080707' }}>{activity.title}</span>
        <span style={{ fontSize: '13px', color: '#706e6b' }}>{activity.duration} min</span>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginTop: '8px' }}>
        {visibleAttendees.map((name, idx) => (
          <div key={idx} title={name} style={{ width: '28px', height: '28px', borderRadius: '50%', backgroundColor: SLDS_COLORS[idx % SLDS_COLORS.length], color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: '600', border: '2px solid white', marginLeft: idx > 0 ? '-6px' : '0' }}>
            {name.split(' ').map((n) => n[0]).join('')}
          </div>
        ))}
        {remaining > 0 && <span style={{ fontSize: '12px', color: '#706e6b', marginLeft: '4px' }}>+{remaining} more</span>}
      </div>
      {expanded && (
        <ExpandedPanel>
          <FieldRow label="Location" value={activity.location} />
          <FieldRow label="Duration" value={`${activity.duration} minutes`} />
          <div style={{ gridColumn: '1 / -1' }}>
            <FieldRow label="Agenda" value={activity.agenda} />
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <p style={{ margin: '0 0 4px 0', fontSize: '13px', fontWeight: '600', color: '#080707' }}>Attendees:</p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
              {activity.attendees.map((name, idx) => (
                <span key={idx} style={{ fontSize: '12px', color: '#3e3e3c', padding: '2px 8px', backgroundColor: 'white', borderRadius: '4px', border: '1px solid #dddbda' }}>{name}</span>
              ))}
            </div>
          </div>
          {activity.actionItems && activity.actionItems.length > 0 && (
            <div style={{ gridColumn: '1 / -1' }}>
              <p style={{ margin: '8px 0 4px 0', fontSize: '13px', fontWeight: '600', color: '#080707' }}>Action Items:</p>
              <ul style={{ margin: 0, paddingLeft: '20px' }}>
                {activity.actionItems.map((item, idx) => (
                  <li key={idx} style={{ fontSize: '12px', color: '#3e3e3c', marginBottom: '2px' }}>{item}</li>
                ))}
              </ul>
            </div>
          )}
        </ExpandedPanel>
      )}
    </div>
  );
};

const TaskCard = ({ activity, expanded, onToggleComplete }) => (
  <div>
    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
      <div
        onClick={(e) => { e.stopPropagation(); onToggleComplete(activity.id); }}
        style={{ width: '18px', height: '18px', borderRadius: '3px', border: activity.completed ? 'none' : '2px solid #dddbda', backgroundColor: activity.completed ? '#2e844a' : 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0 }}
      >
        {activity.completed && <Icon category="utility" name="check" size="xx-small" style={{ fill: 'white' }} />}
      </div>
      <span style={{ fontSize: '14px', fontWeight: '600', color: activity.completed ? '#706e6b' : '#080707', textDecoration: activity.completed ? 'line-through' : 'none' }}>
        {activity.title}
      </span>
      <Badge label={activity.priority} color={PRIORITY_COLORS[activity.priority]} />
      <span style={{ fontSize: '12px', color: '#706e6b' }}>Due: {formatDate(activity.dueDate)}</span>
    </div>
    {expanded && (
      <ExpandedPanel>
        <FieldRow label="Task" value={activity.title} />
        <FieldRow label="Priority" value={activity.priority} />
        <FieldRow label="Due Date" value={formatDate(activity.dueDate)} />
        <FieldRow label="Status" value={activity.completed ? 'Completed' : 'Open'} />
        <div style={{ gridColumn: '1 / -1' }}>
          <FieldRow label="Description" value={activity.description || 'No description'} />
        </div>
        <FieldRow label="Owner" value={activity.user} />
        <FieldRow label="Account" value={activity.account} />
      </ExpandedPanel>
    )}
  </div>
);

const StageChangeCard = ({ activity, expanded }) => (
  <div>
    <div style={{ fontSize: '14px', fontWeight: '600', color: '#080707', marginBottom: '8px' }}>{activity.opportunity}</div>
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
      <span style={{ display: 'inline-block', padding: '4px 12px', borderRadius: '4px', fontSize: '12px', fontWeight: '600', backgroundColor: '#f3f3f3', color: '#3e3e3c', border: '1px solid #dddbda' }}>{activity.fromStage}</span>
      <Icon category="utility" name="forward" size="x-small" style={{ fill: '#706e6b' }} />
      <span style={{ display: 'inline-block', padding: '4px 12px', borderRadius: '4px', fontSize: '12px', fontWeight: '600', backgroundColor: '#2e844a14', color: '#2e844a', border: '1px solid #2e844a33' }}>{activity.toStage}</span>
      <span style={{ fontSize: '14px', fontWeight: '700', color: '#0176d3', marginLeft: '8px' }}>{formatCurrency(activity.amount)}</span>
    </div>
    {expanded && (
      <ExpandedPanel>
        <FieldRow label="Opportunity" value={activity.opportunity} />
        <FieldRow label="Amount" value={formatCurrency(activity.amount)} />
        <FieldRow label="From Stage" value={activity.fromStage} />
        <FieldRow label="To Stage" value={activity.toStage} />
        <FieldRow label="Probability" value={`${activity.probability}%`} />
        <FieldRow label="Changed by" value={activity.user} />
      </ExpandedPanel>
    )}
  </div>
);

const CaseCard = ({ activity, expanded }) => (
  <div>
    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
      <span style={{ fontSize: '13px', color: '#706e6b', fontWeight: '600' }}>{activity.caseNumber}</span>
      <span style={{ fontSize: '14px', fontWeight: '600', color: '#080707' }}>{activity.subject}</span>
      <Badge label={activity.status} color={STATUS_COLORS[activity.status] || '#706e6b'} />
      <Badge label={activity.priority} color={PRIORITY_COLORS[activity.priority]} />
    </div>
    <div style={{ fontSize: '12px', color: '#706e6b', marginTop: '4px' }}>Origin: {activity.origin}</div>
    {expanded && (
      <ExpandedPanel>
        <FieldRow label="Case Number" value={activity.caseNumber} />
        <FieldRow label="Subject" value={activity.subject} />
        <FieldRow label="Status" value={activity.status} />
        <FieldRow label="Priority" value={activity.priority} />
        <FieldRow label="Origin" value={activity.origin} />
        <FieldRow label="Contact" value={activity.contact || 'N/A'} />
        <div style={{ gridColumn: '1 / -1' }}>
          <p style={{ margin: '0 0 4px 0', fontSize: '13px', fontWeight: '600', color: '#080707' }}>SLA Progress</p>
          <div style={{ height: '8px', backgroundColor: '#f3f3f3', borderRadius: '4px', overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${activity.slaProgress}%`, backgroundColor: activity.slaProgress > 80 ? '#c23934' : activity.slaProgress > 50 ? '#fe9339' : '#2e844a', borderRadius: '4px', transition: 'width 0.3s' }} />
          </div>
          <span style={{ fontSize: '11px', color: '#706e6b' }}>{activity.slaProgress}% of SLA consumed</span>
        </div>
      </ExpandedPanel>
    )}
  </div>
);

const OrderCard = ({ activity, expanded }) => (
  <div>
    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
      <span style={{ fontSize: '13px', color: '#706e6b', fontWeight: '600' }}>{activity.orderNumber}</span>
      <span style={{ fontSize: '14px', fontWeight: '700', color: '#0176d3' }}>{formatCurrency(activity.totalAmount)}</span>
      <Badge label={activity.status} color={STATUS_COLORS[activity.status] || '#0d9dda'} />
    </div>
    {expanded && (
      <ExpandedPanel>
        <FieldRow label="Order Number" value={activity.orderNumber} />
        <FieldRow label="Total" value={formatCurrency(activity.totalAmount)} />
        <FieldRow label="Status" value={activity.status} />
        <FieldRow label="Line Items" value={activity.lineItemCount} />
        <FieldRow label="Ship Date" value={formatDate(activity.shipDate)} />
        <FieldRow label="Account" value={activity.account} />
      </ExpandedPanel>
    )}
  </div>
);

const NoteCard = ({ activity, expanded }) => (
  <div>
    <div style={{ fontSize: '14px', fontWeight: '600', color: '#080707' }}>{activity.title}</div>
    <div style={{ fontSize: '13px', color: '#706e6b', marginTop: '4px' }}>{expanded ? '' : (activity.body || '').slice(0, 100) + ((activity.body || '').length > 100 ? '...' : '')}</div>
    {expanded && (
      <ExpandedPanel>
        <div style={{ gridColumn: '1 / -1' }}>
          <FieldRow label="Title" value={activity.title} />
        </div>
        <div style={{ gridColumn: '1 / -1' }}>
          <p style={{ margin: '0 0 4px 0', fontSize: '13px', color: '#3e3e3c', lineHeight: '1.6' }}>{activity.body}</p>
        </div>
        <FieldRow label="Related To" value={activity.relatedTo} />
      </ExpandedPanel>
    )}
  </div>
);

const AttachmentCard = ({ activity, expanded }) => {
  const iconMap = { pdf: '#c23934', docx: '#0176d3', xlsx: '#2e844a', png: '#9050e9', mp4: '#fe9339', pptx: '#fe9339', md: '#706e6b' };
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
        <span style={{ width: '32px', height: '32px', borderRadius: '4px', backgroundColor: (iconMap[activity.fileType] || '#706e6b') + '14', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', fontWeight: '700', color: iconMap[activity.fileType] || '#706e6b', textTransform: 'uppercase', border: `1px solid ${(iconMap[activity.fileType] || '#706e6b')}33` }}>
          {activity.fileType}
        </span>
        <span style={{ fontSize: '14px', fontWeight: '600', color: '#080707' }}>{activity.fileName}</span>
        <span style={{ fontSize: '12px', color: '#706e6b' }}>{activity.fileSize}</span>
      </div>
      {expanded && (
        <ExpandedPanel>
          <FieldRow label="File Name" value={activity.fileName} />
          <FieldRow label="Type" value={activity.fileType?.toUpperCase()} />
          <FieldRow label="Size" value={activity.fileSize} />
          <FieldRow label="Related To" value={activity.relatedTo} />
          <FieldRow label="Uploaded by" value={activity.user} />
          <FieldRow label="Date" value={formatAbsoluteTime(activity.timestamp)} />
        </ExpandedPanel>
      )}
    </div>
  );
};

const SiteVisitCard = ({ activity, expanded }) => (
  <div>
    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
      <span style={{ fontSize: '14px', fontWeight: '600', color: '#080707' }}>{activity.location}</span>
      <span style={{ fontSize: '12px', color: '#706e6b' }}>Met: {activity.contactMet}</span>
      <span style={{ fontSize: '12px', color: '#706e6b' }}>{formatDate(activity.visitDate)}</span>
    </div>
    {expanded && (
      <ExpandedPanel>
        <FieldRow label="Location" value={activity.location} />
        <FieldRow label="Contact Met" value={activity.contactMet} />
        <FieldRow label="Visit Date" value={formatDate(activity.visitDate)} />
        <FieldRow label="Account" value={activity.account} />
        <div style={{ gridColumn: '1 / -1' }}>
          <FieldRow label="Notes" value={activity.notes} />
        </div>
        <FieldRow label="Photos" value={activity.photoCount > 0 ? `${activity.photoCount} photos` : 'None'} />
      </ExpandedPanel>
    )}
  </div>
);

const EscalationCard = ({ activity, expanded }) => (
  <div>
    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
      <span style={{ fontSize: '13px', color: '#706e6b', fontWeight: '600' }}>{activity.caseReference}</span>
      <Badge label={activity.severity} color={SEVERITY_COLORS[activity.severity]} />
      <span style={{ fontSize: '13px', color: '#3e3e3c' }}>{activity.reason}</span>
    </div>
    {expanded && (
      <ExpandedPanel>
        <FieldRow label="Case Reference" value={activity.caseReference} />
        <FieldRow label="Severity" value={activity.severity} />
        <FieldRow label="Reason" value={activity.reason} />
        <FieldRow label="Escalated To" value={activity.escalatedTo} />
        <FieldRow label="Escalated By" value={activity.user} />
        <FieldRow label="Date" value={formatAbsoluteTime(activity.timestamp)} />
      </ExpandedPanel>
    )}
  </div>
);

const SubscriptionChangeCard = ({ activity, expanded }) => {
  const changeColor = CHANGE_TYPE_COLORS[activity.changeType] || '#706e6b';
  const isPositive = activity.mrrImpact > 0;
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
        <Badge label={activity.changeType} color={changeColor} />
        <span style={{ fontSize: '14px', fontWeight: '600', color: '#080707' }}>{activity.product}</span>
        <span style={{ fontSize: '14px', fontWeight: '700', color: isPositive ? '#2e844a' : '#c23934' }}>
          {isPositive ? '+' : ''}{formatCurrency(activity.mrrImpact)} MRR
        </span>
      </div>
      {expanded && (
        <ExpandedPanel>
          <FieldRow label="Change Type" value={activity.changeType} />
          <FieldRow label="Product" value={activity.product} />
          <FieldRow label="MRR Impact" value={`${isPositive ? '+' : ''}${formatCurrency(activity.mrrImpact)}`} />
          <FieldRow label="Old Plan" value={activity.oldPlan} />
          <FieldRow label="New Plan" value={activity.newPlan} />
          <FieldRow label="Effective Date" value={formatDate(activity.effectiveDate)} />
        </ExpandedPanel>
      )}
    </div>
  );
};

const ApprovalCard = ({ activity, expanded }) => (
  <div>
    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
      <span style={{ fontSize: '14px', fontWeight: '600', color: '#080707' }}>{activity.title}</span>
      <Badge label={activity.status} color={STATUS_COLORS[activity.status] || '#706e6b'} />
      <span style={{ fontSize: '12px', color: '#706e6b' }}>by {activity.submittedBy}</span>
    </div>
    {expanded && (
      <ExpandedPanel>
        <FieldRow label="Title" value={activity.title} />
        <FieldRow label="Status" value={activity.status} />
        <FieldRow label="Submitted By" value={activity.submittedBy} />
        <div style={{ gridColumn: '1 / -1' }}>
          <p style={{ margin: '0 0 4px 0', fontSize: '13px', fontWeight: '600', color: '#080707' }}>Approver Chain:</p>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {(activity.approvers || []).map((approver, idx) => (
              <span key={idx} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '4px 10px', borderRadius: '12px', fontSize: '12px', backgroundColor: STATUS_COLORS[approver.status] ? `${STATUS_COLORS[approver.status]}14` : '#f3f3f3', border: `1px solid ${STATUS_COLORS[approver.status] || '#dddbda'}33`, color: STATUS_COLORS[approver.status] || '#3e3e3c' }}>
                {approver.name} — {approver.status}
              </span>
            ))}
          </div>
        </div>
        {activity.comments && <div style={{ gridColumn: '1 / -1' }}><FieldRow label="Comments" value={activity.comments} /></div>}
      </ExpandedPanel>
    )}
  </div>
);

// Card renderer dispatch
const EventCardContent = ({ activity, expanded, onToggleComplete }) => {
  switch (activity.type) {
    case 'call': return <CallCard activity={activity} expanded={expanded} />;
    case 'email': return <EmailCard activity={activity} expanded={expanded} />;
    case 'meeting': return <MeetingCard activity={activity} expanded={expanded} />;
    case 'task': return <TaskCard activity={activity} expanded={expanded} onToggleComplete={onToggleComplete} />;
    case 'stage_change': return <StageChangeCard activity={activity} expanded={expanded} />;
    case 'case': return <CaseCard activity={activity} expanded={expanded} />;
    case 'order': return <OrderCard activity={activity} expanded={expanded} />;
    case 'note': return <NoteCard activity={activity} expanded={expanded} />;
    case 'attachment': return <AttachmentCard activity={activity} expanded={expanded} />;
    case 'site_visit': return <SiteVisitCard activity={activity} expanded={expanded} />;
    case 'escalation': return <EscalationCard activity={activity} expanded={expanded} />;
    case 'subscription_change': return <SubscriptionChangeCard activity={activity} expanded={expanded} />;
    case 'approval': return <ApprovalCard activity={activity} expanded={expanded} />;
    default: return <div style={{ fontSize: '13px', color: '#706e6b' }}>Activity recorded</div>;
  }
};

// ========== 9. Inline Action Bar ==========

const InlineActionBar = ({ activity, onAction }) => {
  const universalActions = [
    { key: 'edit', label: 'Edit', icon: 'edit' },
    { key: 'delete', label: 'Delete', icon: 'delete' },
    { key: 'pin', label: activity.isPinned ? 'Unpin' : 'Pin', icon: 'pin' },
    { key: 'follow_up', label: 'Follow Up', icon: 'task' },
  ];

  const typeActions = {
    email: [{ key: 'reply', label: 'Reply', icon: 'reply' }, { key: 'forward', label: 'Forward', icon: 'forward' }],
    meeting: [{ key: 'reschedule', label: 'Reschedule', icon: 'event' }],
    task: [{ key: 'complete', label: 'Complete', icon: 'check' }],
    case: [{ key: 'escalate', label: 'Escalate', icon: 'warning' }, { key: 'close_case', label: 'Close', icon: 'close' }],
  };

  const actions = [...(typeActions[activity.type] || []), ...universalActions];

  return (
    <div
      style={{ display: 'flex', gap: '4px', marginTop: '8px', paddingTop: '8px', borderTop: '1px solid #f3f3f3' }}
      onClick={(e) => e.stopPropagation()}
    >
      {actions.map((action) => (
        <Tooltip key={action.key} content={action.label} align="top">
          <button
            onClick={() => onAction(activity.id, action.key)}
            style={{ background: 'none', border: '1px solid transparent', borderRadius: '4px', padding: '4px 8px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', color: '#706e6b' }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f3f3f3'; e.currentTarget.style.borderColor = '#dddbda'; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.borderColor = 'transparent'; }}
          >
            <Icon category="utility" name={action.icon} size="xx-small" style={{ fill: '#706e6b' }} />
            <span>{action.label}</span>
          </button>
        </Tooltip>
      ))}
    </div>
  );
};

// ========== 10. Timeline Item Wrapper ==========

const MilestoneMarker = ({ activity }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 20px', margin: '8px 0', backgroundColor: '#fef3cd', border: '1px solid #ffc107', borderRadius: '4px' }}>
    <Icon category="utility" name="favorite" size="small" style={{ fill: '#b8860b' }} />
    <span style={{ fontSize: '14px', fontWeight: '700', color: '#664d03' }}>Milestone: {TYPE_CONFIG[activity.type]?.label} — {activity.user}</span>
    <span style={{ fontSize: '12px', color: '#997404', marginLeft: 'auto' }}>{formatRelativeTime(activity.timestamp)}</span>
  </div>
);

const TimelineItem = ({ activity, expanded, onToggleExpand, onToggleComplete, onAction, includeRelated }) => {
  const [hovered, setHovered] = useState(false);
  const config = TYPE_CONFIG[activity.type] || { color: '#706e6b', icon: 'record', iconCategory: 'standard', label: activity.type };

  return (
    <div style={{ display: 'flex', gap: '16px', marginBottom: '4px', position: 'relative' }}>
      {/* Timeline Icon Circle */}
      <div style={{ width: '48px', height: '48px', minWidth: '48px', borderRadius: '50%', backgroundColor: config.color, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1 }}>
        <Icon category={config.iconCategory} name={config.icon} size="small" style={{ fill: 'white' }} />
      </div>

      {/* Event Card */}
      <div
        onClick={() => onToggleExpand(activity.id)}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        style={{
          flex: 1,
          backgroundColor: 'white',
          border: `1px solid ${hovered ? config.color : '#dddbda'}`,
          borderRadius: '4px',
          padding: '16px',
          cursor: 'pointer',
          transition: 'border-color 0.2s, box-shadow 0.15s',
          borderLeft: `3px solid ${config.color}`,
          boxShadow: hovered ? '0 2px 8px rgba(0,0,0,0.08)' : 'none',
          marginBottom: '16px',
        }}
      >
        {/* Card Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            {/* Avatar */}
            <div style={{ width: '28px', height: '28px', borderRadius: '50%', backgroundColor: config.color + '33', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: '600', color: config.color }}>
              {activity.user.split(' ').map((n) => n[0]).join('')}
            </div>
            <span style={{ fontSize: '13px', fontWeight: '700', color: '#080707' }}>{activity.user}</span>
            <Tooltip content={formatAbsoluteTime(activity.timestamp)}>
              <span style={{ fontSize: '12px', color: '#706e6b', cursor: 'default' }}>{formatRelativeTime(activity.timestamp)}</span>
            </Tooltip>
            {activity.isPinned && <Icon category="utility" name="pin" size="xx-small" style={{ fill: '#fe9339' }} />}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: '600', color: config.color, backgroundColor: config.bgColor, textTransform: 'uppercase', letterSpacing: '0.3px' }}>
              {config.label}
            </span>
            <Icon category="utility" name={expanded ? 'chevronup' : 'chevrondown'} size="xx-small" style={{ fill: '#706e6b' }} />
          </div>
        </div>

        {/* Breadcrumb row for rolled-up items */}
        {includeRelated && activity.parentRecord && (
          <div style={{ marginBottom: '8px', fontSize: '12px', color: '#706e6b' }}>
            <span style={{ color: '#0176d3', cursor: 'pointer' }}>{activity.account}</span>
            <span> &rsaquo; </span>
            <span style={{ color: '#0176d3', cursor: 'pointer' }}>{activity.parentRecord}: {activity.parentRecordName}</span>
          </div>
        )}

        {/* Account link (when not showing breadcrumbs) */}
        {!includeRelated && (
          <div style={{ marginBottom: '8px' }}>
            <span style={{ fontSize: '12px', color: '#0176d3', cursor: 'pointer' }}>{activity.account}</span>
          </div>
        )}

        {/* Card Content */}
        <EventCardContent activity={activity} expanded={expanded} onToggleComplete={onToggleComplete} />

        {/* Inline Actions on hover */}
        {hovered && <InlineActionBar activity={activity} onAction={onAction} />}
      </div>
    </div>
  );
};

// ========== 12. Today Separator & Date Group Headers ==========

const TodaySeparator = () => (
  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', margin: '20px 0 16px 64px' }}>
    <div style={{ flex: 1, height: '1px', backgroundColor: '#0176d3' }} />
    <span style={{ fontSize: '12px', fontWeight: '700', color: '#0176d3', textTransform: 'uppercase', letterSpacing: '0.5px', padding: '4px 12px', backgroundColor: '#eef4ff', borderRadius: '12px', border: '1px solid #b9d6f7' }}>Today</span>
    <div style={{ flex: 1, height: '1px', backgroundColor: '#0176d3' }} />
  </div>
);

const DateGroupHeader = ({ label, count, collapsed, onToggle }) => (
  <div
    onClick={onToggle}
    style={{ display: 'flex', alignItems: 'center', gap: '12px', margin: '20px 0 12px 0', cursor: 'pointer', padding: '8px 16px', backgroundColor: '#f3f3f3', borderRadius: '4px' }}
  >
    <Icon category="utility" name={collapsed ? 'chevronright' : 'chevrondown'} size="xx-small" style={{ fill: '#706e6b' }} />
    <span style={{ fontSize: '13px', fontWeight: '700', color: '#3e3e3c' }}>{label}</span>
    <span style={{ fontSize: '12px', color: '#706e6b' }}>({count} {count === 1 ? 'item' : 'items'})</span>
    <div style={{ flex: 1, height: '1px', backgroundColor: '#dddbda', marginLeft: '8px' }} />
  </div>
);

// ========== 13. Compact View ==========

const TypeIconCell = ({ children, ...props }) => {
  const type = props.item && props.item.type;
  const config = TYPE_CONFIG[type] || {};
  return (
    <DataTableCell {...props}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
        <span style={{ width: '24px', height: '24px', borderRadius: '50%', backgroundColor: config.color || '#706e6b', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
          <Icon category={config.iconCategory || 'standard'} name={config.icon || 'record'} size="xx-small" style={{ fill: 'white' }} />
        </span>
        <span style={{ fontSize: '12px', fontWeight: '600', color: config.color || '#706e6b' }}>{config.label || type}</span>
      </div>
    </DataTableCell>
  );
};
TypeIconCell.displayName = DataTableCell.displayName;

const DescriptionCell = ({ children, ...props }) => {
  const item = props.item;
  const desc = item.subject || item.title || item.opportunity || item.caseNumber || item.orderNumber || item.fileName || item.location || item.caseReference || item.product || 'Activity';
  return (
    <DataTableCell {...props}>
      <span style={{ fontSize: '13px', color: '#080707', fontWeight: '500' }}>{desc}</span>
    </DataTableCell>
  );
};
DescriptionCell.displayName = DataTableCell.displayName;

const PriorityCell = ({ children, ...props }) => {
  const priority = props.item && props.item.priority;
  if (!priority) return <DataTableCell {...props}><span style={{ color: '#b0adab' }}>—</span></DataTableCell>;
  return (
    <DataTableCell {...props}>
      <Badge label={priority} color={PRIORITY_COLORS[priority]} />
    </DataTableCell>
  );
};
PriorityCell.displayName = DataTableCell.displayName;

const StatusCell = ({ children, ...props }) => {
  const item = props.item;
  const status = item.status || item.outcome || (item.completed !== undefined ? (item.completed ? 'Completed' : 'Open') : null);
  if (!status) return <DataTableCell {...props}><span style={{ color: '#b0adab' }}>—</span></DataTableCell>;
  return (
    <DataTableCell {...props}>
      <Badge label={status} color={STATUS_COLORS[status] || OUTCOME_COLORS[status] || '#706e6b'} />
    </DataTableCell>
  );
};
StatusCell.displayName = DataTableCell.displayName;

const DateCell = ({ children, ...props }) => {
  const ts = props.item && props.item.timestamp;
  return (
    <DataTableCell {...props}>
      <span style={{ fontSize: '12px', color: '#706e6b' }}>{ts ? formatRelativeTime(ts) : '—'}</span>
    </DataTableCell>
  );
};
DateCell.displayName = DataTableCell.displayName;

const CompactView = ({ activities, expandedIds, onToggleExpand, onToggleComplete, onAction }) => {
  const [expandedRow, setExpandedRow] = useState(null);

  return (
    <div>
      <DataTable items={activities} id="timeline-compact-table" fixedLayout>
        <DataTableColumn label="Type" property="type" width="140px">
          <TypeIconCell />
        </DataTableColumn>
        <DataTableColumn label="Description" property="description" width="280px">
          <DescriptionCell />
        </DataTableColumn>
        <DataTableColumn label="Account" property="account" width="150px" />
        <DataTableColumn label="User" property="user" width="130px" />
        <DataTableColumn label="Date" property="timestamp" width="100px">
          <DateCell />
        </DataTableColumn>
        <DataTableColumn label="Priority" property="priority" width="100px">
          <PriorityCell />
        </DataTableColumn>
        <DataTableColumn label="Status" property="status" width="120px">
          <StatusCell />
        </DataTableColumn>
      </DataTable>

      {/* Inline detail expansion */}
      {expandedRow && (
        <div style={{ ...cardStyle, margin: '8px 0', borderLeft: `3px solid ${TYPE_CONFIG[expandedRow.type]?.color || '#706e6b'}` }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
            <span style={{ fontSize: '14px', fontWeight: '600', color: '#080707' }}>{TYPE_CONFIG[expandedRow.type]?.label} Detail</span>
            <Button label="Close" variant="base" onClick={() => setExpandedRow(null)} iconCategory="utility" iconName="close" iconPosition="left" iconSize="x-small" />
          </div>
          <EventCardContent activity={expandedRow} expanded={true} onToggleComplete={onToggleComplete} />
        </div>
      )}
    </div>
  );
};

// ========== 14. Visual Timeline ==========

const VisualTimeline = ({ activities }) => {
  const [zoom, setZoom] = useState('week');
  const [hoveredItem, setHoveredItem] = useState(null);

  const timeRange = useMemo(() => {
    const now = REF_NOW;
    if (zoom === 'day') return { start: new Date(now.getTime() - 1 * 86400000), end: now, labels: Array.from({ length: 24 }, (_, i) => `${i}:00`) };
    if (zoom === 'week') return { start: new Date(now.getTime() - 7 * 86400000), end: now, labels: Array.from({ length: 7 }, (_, i) => { const d = new Date(now.getTime() - (6 - i) * 86400000); return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }); }) };
    return { start: new Date(now.getTime() - 30 * 86400000), end: now, labels: Array.from({ length: 30 }, (_, i) => { const d = new Date(now.getTime() - (29 - i) * 86400000); return i % 5 === 0 ? d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : ''; }) };
  }, [zoom]);

  const types = Object.keys(TYPE_CONFIG);
  const rangeMs = timeRange.end - timeRange.start;

  const filteredForRange = activities.filter((a) => {
    const t = new Date(a.timestamp);
    return t >= timeRange.start && t <= timeRange.end;
  });

  return (
    <div style={{ ...cardStyle, overflow: 'hidden' }}>
      {/* Zoom controls */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <h3 style={{ fontSize: '14px', fontWeight: '700', color: '#080707', margin: 0 }}>Visual Timeline</h3>
        <ButtonGroup>
          {[['Day', 'day'], ['Week', 'week'], ['Month', 'month']].map(([label, mode]) => (
            <Button key={mode} label={label} variant={zoom === mode ? 'brand' : 'neutral'} onClick={() => setZoom(mode)} />
          ))}
        </ButtonGroup>
      </div>

      {/* Scrollable container */}
      <div style={{ overflowX: 'auto', position: 'relative' }}>
        <div style={{ minWidth: zoom === 'month' ? '1200px' : '800px', position: 'relative' }}>
          {/* Time axis */}
          <div style={{ display: 'flex', borderBottom: '2px solid #dddbda', paddingBottom: '8px', marginBottom: '4px', marginLeft: '140px' }}>
            {timeRange.labels.map((label, idx) => (
              <div key={idx} style={{ flex: 1, textAlign: 'center', fontSize: '11px', color: '#706e6b', minWidth: '30px' }}>
                {label}
              </div>
            ))}
          </div>

          {/* Now line */}
          <div style={{ position: 'absolute', right: '0', top: '30px', bottom: '0', width: '2px', backgroundColor: '#c23934', zIndex: 5, borderStyle: 'dashed' }}>
            <span style={{ position: 'absolute', top: '-18px', right: '-12px', fontSize: '10px', color: '#c23934', fontWeight: '700' }}>Now</span>
          </div>

          {/* Swim lanes */}
          {types.map((type) => {
            const config = TYPE_CONFIG[type];
            const items = filteredForRange.filter((a) => a.type === type);
            return (
              <div key={type} style={{ display: 'flex', alignItems: 'center', borderBottom: '1px solid #f3f3f3', minHeight: '36px' }}>
                {/* Lane label */}
                <div style={{ width: '140px', flexShrink: 0, paddingRight: '12px', fontSize: '12px', color: '#706e6b', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: config.color, display: 'inline-block' }} />
                  {config.label}
                </div>
                {/* Lane content */}
                <div style={{ flex: 1, position: 'relative', height: '36px' }}>
                  {items.map((item) => {
                    const ts = new Date(item.timestamp);
                    const pct = Math.max(0, Math.min(100, ((ts - timeRange.start) / rangeMs) * 100));
                    const isHovered = hoveredItem === item.id;
                    return (
                      <div
                        key={item.id}
                        onMouseEnter={() => setHoveredItem(item.id)}
                        onMouseLeave={() => setHoveredItem(null)}
                        style={{
                          position: 'absolute',
                          left: `${pct}%`,
                          top: '50%',
                          width: item.isMilestone ? '16px' : '12px',
                          height: item.isMilestone ? '16px' : '12px',
                          borderRadius: item.isMilestone ? '2px' : '50%',
                          backgroundColor: config.color,
                          border: `2px solid white`,
                          boxShadow: isHovered ? `0 0 0 3px ${config.color}44` : '0 1px 2px rgba(0,0,0,0.2)',
                          cursor: 'pointer',
                          zIndex: isHovered ? 10 : 1,
                          transform: item.isMilestone ? 'translate(-50%, -50%) rotate(45deg)' : 'translate(-50%, -50%)',
                        }}
                      >
                        {/* Hover popover */}
                        {isHovered && (
                          <div
                            style={{
                              position: 'absolute',
                              bottom: '100%',
                              left: '50%',
                              transform: item.isMilestone ? 'rotate(-45deg) translate(-50%, -8px)' : 'translate(-50%, -8px)',
                              backgroundColor: 'white',
                              border: '1px solid #dddbda',
                              borderRadius: '4px',
                              padding: '8px 12px',
                              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                              whiteSpace: 'nowrap',
                              zIndex: 100,
                              minWidth: '180px',
                            }}
                          >
                            <div style={{ fontSize: '12px', fontWeight: '600', color: config.color, marginBottom: '4px' }}>{config.label}</div>
                            <div style={{ fontSize: '12px', color: '#3e3e3c' }}>{item.subject || item.title || item.opportunity || item.caseNumber || item.fileName || item.location || 'Activity'}</div>
                            <div style={{ fontSize: '11px', color: '#706e6b', marginTop: '2px' }}>{item.user} &middot; {formatRelativeTime(item.timestamp)}</div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// ========== 15. Main Component ==========

const TimelineActivityFeed = () => {
  // Data
  const initialActivities = useMemo(() => generateTimelineData(60), []);
  const [activities, setActivities] = useState(initialActivities);

  // View state
  const [viewMode, setViewMode] = useState('feed');
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('date_desc');
  const [groupBy, setGroupBy] = useState('none');
  const [includeRelated, setIncludeRelated] = useState(false);
  const [expandedIds, setExpandedIds] = useState({});
  const [expandAll, setExpandAll] = useState(false);
  const [visibleCount, setVisibleCount] = useState(10);
  const [showFilters, setShowFilters] = useState(false);
  const [showQuickAdd, setShowQuickAdd] = useState(false);
  const [collapsedGroups, setCollapsedGroups] = useState({});
  const [addActivityType, setAddActivityType] = useState(null);
  const [logActivityOpen, setLogActivityOpen] = useState(false);
  const [toasts, setToasts] = useState([]);

  // Filters: array of { category, label, value/values }
  const [activeFilters, setActiveFilters] = useState([]);

  // Toast helper
  const addToast = useCallback((message, variant = 'success') => {
    const id = Date.now();
    setToasts((prev) => [...prev, { id, message, variant }]);
    setTimeout(() => { setToasts((prev) => prev.filter((t) => t.id !== id)); }, 4000);
  }, []);

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  // Toggle expand
  const handleToggleExpand = useCallback((id) => {
    setExpandedIds((prev) => ({ ...prev, [id]: !prev[id] }));
  }, []);

  const handleToggleExpandAll = useCallback(() => {
    setExpandAll((prev) => {
      const next = !prev;
      if (next) {
        const all = {};
        activities.forEach((a) => { all[a.id] = true; });
        setExpandedIds(all);
      } else {
        setExpandedIds({});
      }
      return next;
    });
  }, [activities]);

  // Toggle task completion
  const handleToggleComplete = useCallback((id) => {
    setActivities((prev) => prev.map((a) => (a.id === id ? { ...a, completed: !a.completed } : a)));
    addToast('Task status updated');
  }, [addToast]);

  // Inline actions
  const handleAction = useCallback((id, action) => {
    if (action === 'pin') {
      setActivities((prev) => prev.map((a) => (a.id === id ? { ...a, isPinned: !a.isPinned } : a)));
      addToast('Pin status updated');
    } else if (action === 'delete') {
      setActivities((prev) => prev.filter((a) => a.id !== id));
      addToast('Activity deleted', 'warning');
    } else if (action === 'complete') {
      handleToggleComplete(id);
    } else {
      addToast(`Action "${action}" triggered`);
    }
  }, [addToast, handleToggleComplete]);

  // Filter management
  const handleAddFilter = useCallback((filter) => {
    if (filter.category === 'type') {
      // Remove existing type filters, add new
      setActiveFilters((prev) => [
        ...prev.filter((f) => f.category !== 'type'),
        ...filter.values.map((v) => ({ category: 'type', label: `Type: ${TYPE_CONFIG[v]?.label || v}`, value: v })),
      ]);
    } else if (filter.category === 'priority') {
      setActiveFilters((prev) => [
        ...prev,
        ...filter.values.map((v) => ({ category: 'priority', label: `Priority: ${v}`, value: v })),
      ]);
    } else if (filter.category === 'status') {
      setActiveFilters((prev) => [
        ...prev,
        ...filter.values.map((v) => ({ category: 'status', label: `Status: ${v}`, value: v })),
      ]);
    }
    setVisibleCount(10);
  }, []);

  const handleRemoveFilter = useCallback((filter) => {
    setActiveFilters((prev) => prev.filter((f) => !(f.category === filter.category && f.value === filter.value)));
    setVisibleCount(10);
  }, []);

  const handleClearAllFilters = useCallback(() => {
    setActiveFilters([]);
    setSearchTerm('');
    setVisibleCount(10);
  }, []);

  const handleSmartFilter = useCallback((preset) => {
    if (preset === 'needs_attention') {
      setActiveFilters([
        { category: 'smart', label: 'Needs Attention', value: 'needs_attention' },
      ]);
    } else if (preset === 'this_week') {
      setActiveFilters([
        { category: 'smart', label: 'This Week', value: 'this_week' },
      ]);
    } else if (preset === 'my_activities') {
      setActiveFilters([
        { category: 'smart', label: 'My Activities', value: 'my_activities' },
      ]);
    }
    setVisibleCount(10);
  }, []);

  // Quick add — opens the log activity modal
  const handleQuickAdd = useCallback(() => {
    setLogActivityOpen(true);
  }, []);

  const handleSaveNewActivity = useCallback((type, formData) => {
    const config = TYPE_CONFIG[type] || {};
    const newActivity = {
      id: `tl-new-${Date.now()}`,
      type,
      user: 'You',
      timestamp: new Date().toISOString(),
      account: formData.account || 'Acme Corp',
      contact: null,
      parentRecord: 'Account',
      parentRecordName: formData.account || 'Acme Corp',
      isPinned: false,
      isMilestone: false,
      title: formData.title || `New ${config.label}`,
      notes: formData.notes || '',
      duration: formData.duration ? parseInt(formData.duration, 10) : 30,
      priority: 'Medium',
      completed: false,
      status: 'New',
    };
    setActivities((prev) => [newActivity, ...prev]);
    setAddActivityType(null);
    addToast(`${config.label} created successfully`);
  }, [addToast]);

  // ---- Filtering & Sorting Pipeline ----
  const filteredActivities = useMemo(() => {
    let result = [...activities];

    // Apply active filters
    const typeFilters = activeFilters.filter((f) => f.category === 'type').map((f) => f.value);
    const priorityFilters = activeFilters.filter((f) => f.category === 'priority').map((f) => f.value);
    const statusFilters = activeFilters.filter((f) => f.category === 'status').map((f) => f.value);
    const smartFilters = activeFilters.filter((f) => f.category === 'smart').map((f) => f.value);

    if (typeFilters.length > 0) {
      result = result.filter((a) => typeFilters.includes(a.type));
    }
    if (priorityFilters.length > 0) {
      result = result.filter((a) => priorityFilters.includes(a.priority));
    }
    if (statusFilters.length > 0) {
      result = result.filter((a) => statusFilters.includes(a.status) || statusFilters.includes(a.outcome));
    }

    // Smart filters
    smartFilters.forEach((sf) => {
      if (sf === 'needs_attention') {
        result = result.filter((a) => {
          if (a.type === 'task' && !a.completed && a.dueDate && new Date(a.dueDate) < REF_NOW) return true;
          if (a.type === 'case' && ['New', 'Escalated'].includes(a.status) && ['Critical', 'High'].includes(a.priority)) return true;
          if (a.type === 'escalation') return true;
          if (a.type === 'approval' && a.status === 'Pending') return true;
          return false;
        });
      } else if (sf === 'this_week') {
        const weekAgo = new Date(REF_NOW.getTime() - 7 * 86400000);
        result = result.filter((a) => new Date(a.timestamp) >= weekAgo);
      } else if (sf === 'my_activities') {
        // Mock: show first 20 items as "my" activities
        result = result.slice(0, 20);
      }
    });

    // Search
    if (searchTerm.trim()) {
      const lower = searchTerm.toLowerCase();
      result = result.filter((a) => {
        const searchable = [a.user, a.account, a.subject, a.title, a.opportunity, a.notes, a.outcome, a.type, a.caseNumber, a.orderNumber, a.fileName, a.location, a.reason, a.product, a.body, a.caseReference, TYPE_CONFIG[a.type]?.label].filter(Boolean).join(' ').toLowerCase();
        return searchable.includes(lower);
      });
    }

    // Sort
    if (sortBy === 'date_desc') result.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
    else if (sortBy === 'date_asc') result.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
    else if (sortBy === 'priority') result.sort((a, b) => (priorityRank[a.priority] ?? 99) - (priorityRank[b.priority] ?? 99));
    else if (sortBy === 'type') result.sort((a, b) => a.type.localeCompare(b.type));

    return result;
  }, [activities, activeFilters, searchTerm, sortBy]);

  // Pinned items
  const pinnedActivities = useMemo(() => filteredActivities.filter((a) => a.isPinned), [filteredActivities]);
  const unpinnedActivities = useMemo(() => filteredActivities.filter((a) => !a.isPinned), [filteredActivities]);

  // Grouped activities
  const groupedActivities = useMemo(() => groupActivities(unpinnedActivities, groupBy), [unpinnedActivities, groupBy]);

  // Display with load more
  const displayedActivities = unpinnedActivities.slice(0, visibleCount);
  const hasMore = visibleCount < unpinnedActivities.length;

  const handleLoadMore = () => setVisibleCount((prev) => Math.min(prev + 10, unpinnedActivities.length));

  const handleToggleGroup = (label) => {
    setCollapsedGroups((prev) => ({ ...prev, [label]: !prev[label] }));
  };

  // ---- Render Feed View ----
  const renderFeedView = () => {
    // Check if we need a today separator
    const todayItems = unpinnedActivities.filter((a) => getDateGroup(a.timestamp) === 'Today');
    const showTodaySeparator = todayItems.length > 0 && groupBy === 'none';

    return (
      <div style={{ position: 'relative', paddingLeft: '0' }}>
        {/* Vertical Line */}
        <div style={{ position: 'absolute', left: '24px', top: '0', bottom: '0', width: '2px', backgroundColor: '#dddbda', zIndex: 0 }} />

        {/* Pinned Section */}
        {pinnedActivities.length > 0 && (
          <div style={{ marginBottom: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', margin: '0 0 12px 64px' }}>
              <Icon category="utility" name="pin" size="x-small" style={{ fill: '#fe9339' }} />
              <span style={{ fontSize: '13px', fontWeight: '700', color: '#fe9339', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Pinned ({pinnedActivities.length})</span>
            </div>
            {pinnedActivities.map((activity) => (
              <React.Fragment key={activity.id}>
                {activity.isMilestone && <MilestoneMarker activity={activity} />}
                <TimelineItem
                  activity={activity}
                  expanded={!!expandedIds[activity.id]}
                  onToggleExpand={handleToggleExpand}
                  onToggleComplete={handleToggleComplete}
                  onAction={handleAction}
                  includeRelated={includeRelated}
                />
              </React.Fragment>
            ))}
          </div>
        )}

        {/* Grouped or flat list */}
        {groupBy !== 'none' ? (
          groupedActivities.map((group) => (
            <div key={group.label}>
              <DateGroupHeader
                label={group.label}
                count={group.count}
                collapsed={!!collapsedGroups[group.label]}
                onToggle={() => handleToggleGroup(group.label)}
              />
              {!collapsedGroups[group.label] && group.items.slice(0, visibleCount).map((activity) => (
                <React.Fragment key={activity.id}>
                  {activity.isMilestone && <MilestoneMarker activity={activity} />}
                  <TimelineItem
                    activity={activity}
                    expanded={!!expandedIds[activity.id]}
                    onToggleExpand={handleToggleExpand}
                    onToggleComplete={handleToggleComplete}
                    onAction={handleAction}
                    includeRelated={includeRelated}
                  />
                </React.Fragment>
              ))}
            </div>
          ))
        ) : (
          <>
            {showTodaySeparator && <TodaySeparator />}
            {displayedActivities.length === 0 ? (
              <div style={{ ...cardStyle, textAlign: 'center', padding: '48px 20px', marginLeft: '64px' }}>
                <Icon category="utility" name="info" size="large" style={{ fill: '#dddbda', marginBottom: '12px' }} />
                <p style={{ fontSize: '16px', color: '#706e6b', margin: '12px 0 0 0' }}>No activities match your filters.</p>
                <p style={{ fontSize: '13px', color: '#b0adab', margin: '8px 0 0 0' }}>Try adjusting your filters or search term.</p>
              </div>
            ) : (
              displayedActivities.map((activity) => (
                <React.Fragment key={activity.id}>
                  {activity.isMilestone && <MilestoneMarker activity={activity} />}
                  <TimelineItem
                    activity={activity}
                    expanded={!!expandedIds[activity.id]}
                    onToggleExpand={handleToggleExpand}
                    onToggleComplete={handleToggleComplete}
                    onAction={handleAction}
                    includeRelated={includeRelated}
                  />
                </React.Fragment>
              ))
            )}
          </>
        )}

        {/* Load More */}
        {hasMore && groupBy === 'none' && (
          <div style={{ textAlign: 'center', marginTop: '8px', paddingBottom: '24px' }}>
            <Button
              label={`Load More (${unpinnedActivities.length - visibleCount} remaining)`}
              variant="neutral"
              onClick={handleLoadMore}
              iconCategory="utility"
              iconName="chevrondown"
              iconPosition="right"
              iconSize="x-small"
            />
          </div>
        )}

        {/* End of timeline */}
        {!hasMore && displayedActivities.length > 0 && groupBy === 'none' && (
          <div style={{ textAlign: 'center', padding: '24px 0', borderTop: '1px solid #dddbda', marginTop: '8px' }}>
            <span style={{ fontSize: '13px', color: '#b0adab' }}>
              End of timeline — {filteredActivities.length} activit{filteredActivities.length === 1 ? 'y' : 'ies'} shown
            </span>
          </div>
        )}
      </div>
    );
  };

  return (
    <ShowcasePageWrapper
      title="Timeline & Activity Feed"
      description="Showcase-quality timeline with 13 record types, smart filters, and 3 view modes"
      icon="log_a_call"
      iconCategory="standard"
    >
      {/* Toasts */}
      {toasts.length > 0 && (
        <ToastContainer>
          {toasts.map((toast) => (
            <Toast key={toast.id} labels={{ heading: toast.message }} variant={toast.variant} duration={4000} onRequestClose={() => removeToast(toast.id)} />
          ))}
        </ToastContainer>
      )}

      {/* Action Bar */}
      <div style={{ ...cardStyle, marginBottom: '16px', padding: '12px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontSize: '14px', fontWeight: '600', color: '#3e3e3c' }}>{filteredActivities.length} Activities</span>
        <div style={{ display: 'flex', gap: '8px' }}>
          <Button label="Log Activity" variant="brand" onClick={() => setLogActivityOpen(true)} iconCategory="utility" iconName="add" iconPosition="left" />
          <Button label="Refresh" variant="neutral" onClick={() => setActivities(generateTimelineData(60))} iconCategory="utility" iconName="refresh" iconPosition="left" />
        </div>
      </div>

      {/* Analytics Summary Panel */}
      <AnalyticsSummaryPanel activities={activities} visible={showAnalytics} onToggle={() => setShowAnalytics((p) => !p)} />

      {/* Filter Bar — collapsible */}
      {!showFilters ? (
        <div style={{ ...cardStyle, marginBottom: '16px', padding: '12px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '13px', fontWeight: '600', color: '#3e3e3c' }}>Filters & Search</span>
            {activeFilters.length > 0 && (
              <span style={{ fontSize: '11px', color: 'white', backgroundColor: '#0176d3', borderRadius: '10px', padding: '1px 8px', fontWeight: '600' }}>{activeFilters.length}</span>
            )}
          </div>
          <Button label="Show" variant="base" onClick={() => setShowFilters(true)} iconCategory="utility" iconName="chevrondown" iconPosition="right" iconSize="x-small" />
        </div>
      ) : (
        <div style={{ position: 'relative' }}>
          <div style={{ position: 'absolute', top: '12px', right: '20px', zIndex: 2 }}>
            <Button label="Hide" variant="base" onClick={() => setShowFilters(false)} iconCategory="utility" iconName="chevronup" iconPosition="right" iconSize="x-small" />
          </div>
          <FilterBar
            activeFilters={activeFilters}
            onRemoveFilter={handleRemoveFilter}
            onClearAll={handleClearAllFilters}
            onAddFilter={handleAddFilter}
            onSmartFilter={handleSmartFilter}
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
          />
        </div>
      )}

      {/* Sort & Group Bar — always visible (compact) */}
      <SortGroupBar
        sortBy={sortBy}
        onSortChange={setSortBy}
        groupBy={groupBy}
        onGroupChange={setGroupBy}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        includeRelated={includeRelated}
        onToggleRelated={() => setIncludeRelated((p) => !p)}
        totalCount={filteredActivities.length}
        expandAll={expandAll}
        onToggleExpandAll={handleToggleExpandAll}
      />

      {/* Quick Add Bar — collapsible */}
      {!showQuickAdd ? (
        <div style={{ ...cardStyle, marginBottom: '16px', padding: '10px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: '13px', fontWeight: '600', color: '#3e3e3c' }}>Quick Add</span>
          <Button label="Show" variant="base" onClick={() => setShowQuickAdd(true)} iconCategory="utility" iconName="chevrondown" iconPosition="right" iconSize="x-small" />
        </div>
      ) : (
        <div style={{ position: 'relative' }}>
          <div style={{ position: 'absolute', top: '10px', right: '20px', zIndex: 2 }}>
            <Button label="Hide" variant="base" onClick={() => setShowQuickAdd(false)} iconCategory="utility" iconName="chevronup" iconPosition="right" iconSize="x-small" />
          </div>
          <QuickAddBar onQuickAdd={handleQuickAdd} />
        </div>
      )}

      {/* Log Activity Modal (2-step) */}
      <LogActivityModal
        isOpen={logActivityOpen || !!addActivityType}
        onClose={() => { setLogActivityOpen(false); setAddActivityType(null); }}
        onSave={handleSaveNewActivity}
      />

      {/* Main Content */}
      {viewMode === 'feed' && renderFeedView()}
      {viewMode === 'compact' && (
        <CompactView
          activities={filteredActivities}
          expandedIds={expandedIds}
          onToggleExpand={handleToggleExpand}
          onToggleComplete={handleToggleComplete}
          onAction={handleAction}
        />
      )}
      {viewMode === 'visual' && <VisualTimeline activities={filteredActivities} />}
    </ShowcasePageWrapper>
  );
};

export default TimelineActivityFeed;
